import { RESULT_STATUS, getErrorResult } from "./result";
import { isModuleRegistered } from "SharedStores/util";
import { PROGRAM_TYPES, PROGRAM_NAMESPACE } from "SharedStores/Program/programStore";
import { PROJECT_TYPES, PROJECT_NAMESPACE } from "SharedStores/Project/projectStore";
import { HelperEvaluator } from "./helper";
import { isFiniteNumber, convertToFiniteNumber } from "Util/util";
import { LOGBASE_OPTIONS, EXPRESSION_PROPERTY_NAMES } from "Formula/metadata.js";

export class NumericEvaluator extends HelperEvaluator {
    constructor(formulaEvaluator, storeContext) {
        super(formulaEvaluator, storeContext);
    }
    async add(exp) {
        const left = await this.evalNum(exp.Children[0]);
        const right = await this.evalNum(exp.Children[1]);

        const error = getErrorResult([left, right]);
        if (error) {
            return error;
        }
        const value = left.Value + right.Value;

        return { Value: value };
    }

    async subtract(exp) {
        const left = await this.evalNum(exp.Children[0]);
        const right = await this.evalNum(exp.Children[1]);

        const error = getErrorResult([left, right]);
        if (error) {
            return error;
        }
        const value = left.Value - right.Value;

        return { Value: value };
    }

    async multiply(exp) {
        const left = await this.evalNum(exp.Children[0]);
        const right = await this.evalNum(exp.Children[1]);

        const error = getErrorResult([left, right]);
        if (error) {
            return error;
        }
        const value = left.Value * right.Value;

        return { Value: value };
    }

    async divide(exp) {
        const left = await this.evalNum(exp.Children[0]);
        const right = await this.evalNum(exp.Children[1]);

        const error = getErrorResult([left, right]);
        if (error) {
            return error;
        }
        if (right.Value === 0) {
            exp.inError = true;
            exp.errorMessage = "Tried to divide by zero.";
            return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
        }
        const value = left.Value / right.Value;

        return { Value: value };
    }

    async min(exp) {
        const left = await this.evalNum(exp.Children[0]);
        const right = await this.evalNum(exp.Children[1]);

        const error = getErrorResult([left, right]);
        if (error) {
            return error;
        }

        const value = Math.min(left.Value, right.Value);

        return { Value: value };
    }

    async max(exp) {
        const left = await this.evalNum(exp.Children[0]);
        const right = await this.evalNum(exp.Children[1]);

        const error = getErrorResult([left, right]);
        if (error) {
            return error;
        }

        const value = Math.max(left.Value, right.Value);

        return { Value: value };
    }

    async exponent(exp) {
        const power = await this.evalNum(exp.Children[0]);
        let value;
        let error;
        const isBaseComputed = this.getMetadata(exp).numOperands === 2;

        // y ^ x
        if (isBaseComputed) {
            const base = await this.evalNum(exp.Children[1]);

            error = getErrorResult([power, base]);
            if (error) {
                return error;
            }
            value = Math.pow(base.Value, power.Value);
        }
        // e ^ x
        else {
            error = getErrorResult([power]);
            if (error) {
                return error;
            }
            value = Math.exp(power.Value);
        }

        return { Value: value };
    }

    async logarithm(exp) {
        const argument = await this.evalNum(exp.Children[0]);

        const error = getErrorResult([argument]);
        if (error) {
            return error;
        }

        if (argument.Value <= 0) {
            exp.inError = true;
            exp.errorMessage = "Cannot get the log of a non-positive number";
            return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
        }

        const isBaseE = exp.PropertyDictionary.logbase === LOGBASE_OPTIONS.euler;

        // Math.log(x) ==> ln(x)
        // IE doesn't support Math.log10(x), but there's an easy polyfill:
        // log10(x) ==> ln(x) / ln(10) ==> ln(x) * log10(e)
        const value = isBaseE ? Math.log(argument.Value) : Math.log(argument.Value) * Math.LOG10E;

        return { Value: value };
    }

    async round(exp) {
        const valueToRound = await this.evalNum(exp.Children[0]);

        const error = getErrorResult([valueToRound]);
        if (error) {
            return error;
        }

        const roundingAccuracy = exp.PropertyDictionary.roundingAccuracy;
        const roundingFactor = Math.pow(10, roundingAccuracy);
        const result = Math.round(valueToRound.Value * roundingFactor) / roundingFactor;

        return { Value: result };
    }

    async dateDifference(exp) {
        const left = await this.evalDate(exp.Children[0]);
        const right = await this.evalDate(exp.Children[1]);

        const error = getErrorResult([left, right]);
        if (error) {
            return error;
        }

        const timeUnits = exp.PropertyDictionary[EXPRESSION_PROPERTY_NAMES.DateArithmeticTimeUnits];
        const rangeInclusive = exp.PropertyDictionary[EXPRESSION_PROPERTY_NAMES.DateRangeInclusive] === "true";

        const leftBeforeRight = left.Value.isBefore(right.Value);
        let startDate = leftBeforeRight ? left.Value : right.Value;
        let endDate = leftBeforeRight ? right.Value : left.Value;

        if (rangeInclusive) {
            // If the range is inclusive, we'll add one day to the end and do the non-inclusive calculation.
            endDate = endDate.addDays(1);
        } else if (!leftBeforeRight) {
            // If the range is not inclusive and the left date is after the right date, we need to add one day to both sides to include the end date but not the start date.
            startDate = startDate.addDays(1);
            endDate = endDate.addDays(1);
        }

        const nativeStartDate = new Date(startDate.Year, startDate.Month - 1, startDate.Day);
        const nativeEndDate = new Date(endDate.Year, endDate.Month - 1, endDate.Day);
        const differenceDays = (nativeEndDate - nativeStartDate) / 86400000; // 86400000 = 24 * 60 * 60 * 1000

        let difference;
        switch (timeUnits) {
            case "business":
                try {
                    difference =
                        differenceDays -
                        this.getters[PROGRAM_TYPES.getters.getNumberNonBusinessDaysBetween](startDate, endDate);
                } catch (e) {
                    exp.inError = true;
                    exp.errorMessage = "Invalid date range.";
                    return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
                }
                break;
            case "calendar":
                difference = differenceDays;
                break;
            default:
                throw `evaluateDateDifference: invalid value ${timeUnits} for property timeunits`;
        }

        return { Value: leftBeforeRight ? difference : difference * -1 };
    }

    evaluateConstant(exp) {
        return { Value: exp.ConstantValue };
    }

    evaluateDataReference(exp) {
        exp.usingDefault = false;
        if (!isModuleRegistered(PROGRAM_NAMESPACE, this.state)) {
            throw Error("program store not registered");
        }
        if (isModuleRegistered(PROJECT_NAMESPACE, this.state)) {
            const valStr =
                this.getters[PROJECT_TYPES.getters.dataFieldValueFromId](exp.PublicDataFieldDefinitionId) || null;

            const val = convertToFiniteNumber(valStr);
            if (isFiniteNumber(val)) {
                return { Value: val };
            } else if (isFiniteNumber(exp.ConstantValue)) {
                exp.usingDefault = true;
                return { Value: exp.ConstantValue };
            }
            exp.inError = true;
            const dfd = this.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](exp.PublicDataFieldDefinitionId);
            exp.errorMessage = `No value for datafield ${dfd.Name}.`;
            return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
        }

        exp.inError = true;
        exp.errorMessage = "Project context not found to evaluate data field reference.";
        return { Status: RESULT_STATUS.Error, Message: exp.errorMessage };
    }

    async evaluateConditionalNumeric(exp) {
        const condition = await this.evalBool(exp.Children[0]);
        const left = await this.evalNum(exp.Children[1]);
        const right = await this.evalNum(exp.Children[2]);

        const error = getErrorResult([condition, left, right]);
        if (error) {
            return error;
        }

        const value = condition.Value ? left.Value : right.Value;

        return { Value: value };
    }
}
